import _sortBy from 'lodash/sortBy'
import _cloneDeep from 'lodash/cloneDeep'
import { makeRegex, matcherList, topRatedMatcherList } from './helpers'

const useInsuranceSplit = (
  providerInsurances: ProviderInsurance[],
  selectedInsurance = ''
): ProviderInsurance[][] => {
  const sortedInsurances = getSortedInsurances(providerInsurances)

  const formattedSortedInsurances = []

  /** move user selected insurance to top if it exists and the provider supports it. So that
   * it is selected among the top rated insurances first. Else we run a risk of filling the max allowed top
   * rated insurance (currently 5) before we reach selected insurance
   */
  for (const insurance of sortedInsurances) {
    if (insurance.insuranceProvider.id === selectedInsurance) {
      formattedSortedInsurances.unshift(insurance)
    } else {
      formattedSortedInsurances.push(insurance)
    }
  }

  const [
    highlightedInsurances,
    listDisplayInsurances,
  ] = makeTwoSeparateListsOfInsurances(
    formattedSortedInsurances,
    selectedInsurance
  )

  return [highlightedInsurances, listDisplayInsurances]
}

const makeTwoSeparateListsOfInsurances = (
  sortedInsurances,
  selectedInsurance
) => {
  const maxHighlightedInsurances = 5
  const topRatedRegex = makeRegex('multistring', topRatedMatcherList, null)
  const clonedGroupedInsurance = _cloneDeep(matcherList)

  const [topInsurances, remainingInsurances] = sortedInsurances.reduce(
    ([a, b], { insuranceProvider }) => {
      const isUserSelectedInsurance =
        insuranceProvider?.id === selectedInsurance

      if (
        (topRatedRegex.test(insuranceProvider?.name) ||
          isUserSelectedInsurance) &&
        a.length < maxHighlightedInsurances
      ) {
        const indexInGroupedInsurance = clonedGroupedInsurance.findIndex(
          ({ matcher }) => {
            const groupRegex = makeRegex(
              'multistring',
              matcher.match,
              matcher.exclude
            )
            return groupRegex.test(insuranceProvider?.name)
          }
        )
        updateGroupedInsuranceInfo(
          indexInGroupedInsurance,
          clonedGroupedInsurance,
          insuranceProvider,
          a,
          b,
          isUserSelectedInsurance
        )
      } else {
        // not top rated
        b.push({ insuranceProvider })
      }

      return [a, b]
    },
    [[], []]
  )
  const topInsurancesCount = topInsurances.length

  if (topInsurancesCount >= 2) {
    // bring the user selected insurance/ group to the top of the highlighted list before displaying
    bringUserSelectedInsuranceToTheTop(selectedInsurance, topInsurances)
  }

  if (topInsurancesCount > 0) {
    return [topInsurances, remainingInsurances] as ProviderInsurance[][]
  }

  return [[], sortedInsurances]
}

const getSortedInsurances = (providerInsurances) => {
  const filteredProviderInsurances = providerInsurances?.filter(
    (insurance) => insurance?.insuranceProvider?.name !== 'SelfPay'
  )
  const insurancesSorted = _sortBy(filteredProviderInsurances, [
    (providerInsurance) =>
      providerInsurance.insuranceProvider?.name?.toLowerCase(),
  ])

  return insurancesSorted
}

const updateGroupedInsuranceInfo = (
  indexInGroupedInsurance,
  clonedGroupedInsurance,
  insuranceProvider,
  highlightedInsuranceArray,
  listDisplayInsuranceArray,
  isUserSelectedInsurance
) => {
  if (
    indexInGroupedInsurance !== -1 &&
    clonedGroupedInsurance[indexInGroupedInsurance].isGrouped === null
  ) {
    // is first identified in a group and is userSelected
    // modifying the display name of highlighted for UI
    const highlightedInsurance = {
      ...insuranceProvider,
      name: clonedGroupedInsurance[indexInGroupedInsurance].alias,
    }

    highlightedInsuranceArray.push({ insuranceProvider: highlightedInsurance })
    listDisplayInsuranceArray.push({ insuranceProvider })

    clonedGroupedInsurance[indexInGroupedInsurance].isGrouped = true
    clonedGroupedInsurance[indexInGroupedInsurance].idToBeHighlighted =
      highlightedInsurance.id
  } else if (indexInGroupedInsurance === -1) {
    // is top rated but not in a group
    highlightedInsuranceArray.push({ insuranceProvider: insuranceProvider })
  } else {
    // group already started, add the grouped to the remaining list but...
    // if it's a user selected insurance, change id of insurance to be highlighted to this one
    updateHighlightedIdToUserSelectedId(
      isUserSelectedInsurance,
      clonedGroupedInsurance,
      indexInGroupedInsurance,
      highlightedInsuranceArray,
      insuranceProvider
    )

    listDisplayInsuranceArray.push({ insuranceProvider })
  }
}

const updateHighlightedIdToUserSelectedId = (
  isUserSelectedInsurance,
  clonedGroupedInsurance,
  indexInGroupedInsurance,
  highlightedInsuranceArray,
  insuranceProvider
) => {
  if (isUserSelectedInsurance) {
    const foundIndex = highlightedInsuranceArray.findIndex(
      ({ insuranceProvider }) =>
        insuranceProvider.id ===
        clonedGroupedInsurance[indexInGroupedInsurance].idToBeHighlighted
    )

    if (foundIndex !== -1) {
      highlightedInsuranceArray[foundIndex].insuranceProvider.id =
        insuranceProvider.id
    }
  }
}

const bringUserSelectedInsuranceToTheTop = (
  selectedInsurance,
  highlightedInsurances
) => {
  const userSelectedInsuranceIndex = highlightedInsurances.findIndex(
    ({ insuranceProvider }) => insuranceProvider?.id === selectedInsurance
  )

  if (userSelectedInsuranceIndex !== -1) {
    const userSelectedInsurance = highlightedInsurances.splice(
      userSelectedInsuranceIndex,
      1
    )
    highlightedInsurances.splice(0, 0, userSelectedInsurance[0])
  }
}

export default useInsuranceSplit
